import React, { useEffect, useMemo, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import { QUICK_RANGES, computeQuickRange } from "../../../utils/dateRanges";

function loadFromKey(storageKey) {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return {
      activeRange: parsed?.activeRange ?? "last7",
      startDate: parsed?.startDate ? new Date(parsed.startDate) : null,
      endDate: parsed?.endDate ? new Date(parsed.endDate) : null,
    };
  } catch {
    return null;
  }
}

function saveToKey(storageKey, { activeRange, startDate, endDate }) {
  localStorage.setItem(
    storageKey,
    JSON.stringify({
      activeRange: activeRange ?? null,
      startDate: startDate ? startDate.toISOString() : null,
      endDate: endDate ? endDate.toISOString() : null,
    })
  );
}

export function DateRangeFilter({ storageKey, defaultRangeKey = "last7", onChange }) {
  const persisted = useMemo(() => loadFromKey(storageKey), [storageKey]);

    const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const initial = useMemo(() => {
    if (persisted?.startDate || persisted?.endDate) return persisted;

    const { startDate, endDate } = computeQuickRange(defaultRangeKey);
    return { activeRange: defaultRangeKey, startDate, endDate };
  }, [persisted, defaultRangeKey]);

  const [startDate, setStartDate] = useState(initial.startDate);
  const [endDate, setEndDate] = useState(initial.endDate);
  const [activeRange, setActiveRange] = useState(initial.activeRange);

  useEffect(() => {
    const payload = { startDate, endDate, activeRange };
    saveToKey(storageKey, payload);

    // ✅ usa el ref (no depende de la identidad del callback)
    onChangeRef.current?.(payload);

    // 🔥 OJO: NO pongas `onChange` en deps aquí
  }, [startDate, endDate, activeRange, storageKey]);

  const handleClickRange = (key) => {
    const r = computeQuickRange(key);
    setStartDate(r.startDate);
    setEndDate(r.endDate);
    setActiveRange(key);
  };

  const handleManualChange = ([start, end]) => {
    setStartDate(start ?? null);
    setEndDate(end ?? null);
    setActiveRange(null);
  };

  const clear = () => {
    setStartDate(null);
    setEndDate(null);
    setActiveRange(null);
  };

  return (
    <div className="button-dashboard mr-3">
      <DatePicker
        selectsRange
        startDate={startDate}
        endDate={endDate}
        onChange={handleManualChange}
        dateFormat="dd/MM/yyyy"
        placeholderText="Rango de fechas"
      />

      {Object.entries(QUICK_RANGES).map(([key, { label }]) => (
        <button
          key={key}
          type="button"
          className={activeRange === key ? "active" : ""}
          onClick={() => handleClickRange(key)}
        >
          {label}
        </button>
      ))}

      <button type="button" onClick={clear}>
        Limpiar
      </button>
    </div>
  );
}
